import React, { useState } from "react";

/**
 * Sticker Picker, replaces the avatar system in the profile setup flow.
 * Cartoon-style emoji stickers across two themes (animals + sports).
 *
 * Why emoji and not custom illustrations? Emojis render natively as
 * cartoon-style across every modern browser/OS, ship at zero asset cost,
 * are accessible (screen-reader friendly), and avoid the "uncanny human"
 * look the founder explicitly called out. We can swap to a curated SVG
 * sprite sheet later without changing the data model, `sticker` stores
 * an opaque string ID.
 *
 * The avatar system code (AvatarBuilder, /profile/avatar, AI Avatar) is
 * NOT deleted, it stays in the codebase, hidden from the UI, ready to
 * be re-enabled by flipping a feature flag in the future.
 */

// Top 20 popular animals, cartoon-style emoji stickers
export const ANIMAL_STICKERS = [
  { id: "dog", emoji: "🐶", label: "Dog" },
  { id: "cat", emoji: "🐱", label: "Cat" },
  { id: "panda", emoji: "🐼", label: "Panda" },
  { id: "fox", emoji: "🦊", label: "Fox" },
  { id: "lion", emoji: "🦁", label: "Lion" },
  { id: "tiger", emoji: "🐯", label: "Tiger" },
  { id: "koala", emoji: "🐨", label: "Koala" },
  { id: "rabbit", emoji: "🐰", label: "Rabbit" },
  { id: "bear", emoji: "🐻", label: "Bear" },
  { id: "frog", emoji: "🐸", label: "Frog" },
  { id: "monkey", emoji: "🐵", label: "Monkey" },
  { id: "unicorn", emoji: "🦄", label: "Unicorn" },
  { id: "dragon", emoji: "🐲", label: "Dragon" },
  { id: "wolf", emoji: "🐺", label: "Wolf" },
  { id: "giraffe", emoji: "🦒", label: "Giraffe" },
  { id: "turtle", emoji: "🐢", label: "Turtle" },
  { id: "dolphin", emoji: "🐬", label: "Dolphin" },
  { id: "butterfly", emoji: "🦋", label: "Butterfly" },
  { id: "bird", emoji: "🐦", label: "Bird" },
  { id: "whale", emoji: "🐳", label: "Whale" },
];

// 20 sports stickers, balls + activity icons
export const SPORT_STICKERS = [
  { id: "soccer", emoji: "⚽", label: "Soccer" },
  { id: "basketball", emoji: "🏀", label: "Basketball" },
  { id: "football", emoji: "🏈", label: "Football" },
  { id: "baseball", emoji: "⚾", label: "Baseball" },
  { id: "tennis", emoji: "🎾", label: "Tennis" },
  { id: "volleyball", emoji: "🏐", label: "Volleyball" },
  { id: "rugby", emoji: "🏉", label: "Rugby" },
  { id: "billiards", emoji: "🎱", label: "Billiards" },
  { id: "ping-pong", emoji: "🏓", label: "Ping Pong" },
  { id: "badminton", emoji: "🏸", label: "Badminton" },
  { id: "frisbee", emoji: "🥏", label: "Frisbee" },
  { id: "boxing", emoji: "🥊", label: "Boxing" },
  { id: "hockey", emoji: "🏒", label: "Hockey" },
  { id: "field-hockey", emoji: "🏑", label: "Field Hockey" },
  { id: "martial-arts", emoji: "🥋", label: "Martial Arts" },
  { id: "skateboard", emoji: "🛹", label: "Skateboard" },
  { id: "cycling", emoji: "🚴", label: "Cycling" },
  { id: "running", emoji: "🏃", label: "Running" },
  { id: "swimming", emoji: "🏊", label: "Swimming" },
  { id: "trophy", emoji: "🏆", label: "Champion" },
];

// 20 environment / nature stickers, trees, ocean, weather, terrain
export const ENVIRONMENT_STICKERS = [
  { id: "tree", emoji: "🌳", label: "Tree" },
  { id: "evergreen", emoji: "🌲", label: "Evergreen" },
  { id: "palm", emoji: "🌴", label: "Palm Tree" },
  { id: "cactus", emoji: "🌵", label: "Cactus" },
  { id: "flower", emoji: "🌸", label: "Cherry Blossom" },
  { id: "sunflower", emoji: "🌻", label: "Sunflower" },
  { id: "rose", emoji: "🌹", label: "Rose" },
  { id: "tulip", emoji: "🌷", label: "Tulip" },
  { id: "leaf", emoji: "🍃", label: "Leaf" },
  { id: "mushroom", emoji: "🍄", label: "Mushroom" },
  { id: "ocean", emoji: "🌊", label: "Ocean Wave" },
  { id: "cloud", emoji: "☁️", label: "Cloud" },
  { id: "sun", emoji: "☀️", label: "Sun" },
  { id: "rainbow", emoji: "🌈", label: "Rainbow" },
  { id: "snowflake", emoji: "❄️", label: "Snowflake" },
  { id: "lightning", emoji: "⚡", label: "Lightning" },
  { id: "fire", emoji: "🔥", label: "Fire" },
  { id: "mountain", emoji: "🏔️", label: "Mountain" },
  { id: "volcano", emoji: "🌋", label: "Volcano" },
  { id: "earth", emoji: "🌍", label: "Earth" },
];

// 20 fantasy / magic / cosmic stickers, the imagination tier
export const FANTASY_STICKERS = [
  { id: "rocket", emoji: "🚀", label: "Rocket" },
  { id: "planet", emoji: "🪐", label: "Planet" },
  { id: "star", emoji: "⭐", label: "Star" },
  { id: "comet", emoji: "☄️", label: "Comet" },
  { id: "moon", emoji: "🌙", label: "Moon" },
  { id: "alien", emoji: "👽", label: "Alien" },
  { id: "ufo", emoji: "🛸", label: "UFO" },
  { id: "robot", emoji: "🤖", label: "Robot" },
  { id: "ghost", emoji: "👻", label: "Ghost" },
  { id: "wizard", emoji: "🧙", label: "Wizard" },
  { id: "fairy", emoji: "🧚", label: "Fairy" },
  { id: "mermaid", emoji: "🧜", label: "Mermaid" },
  { id: "crown", emoji: "👑", label: "Crown" },
  { id: "gem", emoji: "💎", label: "Gem" },
  { id: "magic-wand", emoji: "🪄", label: "Magic Wand" },
  { id: "crystal-ball", emoji: "🔮", label: "Crystal Ball" },
  { id: "sparkles", emoji: "✨", label: "Sparkles" },
  { id: "shooting-star", emoji: "🌠", label: "Shooting Star" },
  { id: "milky-way", emoji: "🌌", label: "Galaxy" },
  { id: "balloon", emoji: "🎈", label: "Balloon" },
];

const TABS = [
  { id: "animals", label: "Animals", stickers: ANIMAL_STICKERS },
  { id: "sports", label: "Sports", stickers: SPORT_STICKERS },
  { id: "nature", label: "Nature", stickers: ENVIRONMENT_STICKERS },
  { id: "fantasy", label: "Fantasy", stickers: FANTASY_STICKERS },
];

/**
 * Look up the emoji for a stored sticker ID. Returns null for unknown
 * IDs (so callers can render a fallback initial).
 */
export const getStickerEmoji = (id) => {
  if (!id) return null;
  const all = [...ANIMAL_STICKERS, ...SPORT_STICKERS, ...ENVIRONMENT_STICKERS, ...FANTASY_STICKERS];
  return all.find((s) => s.id === id)?.emoji || null;
};

const StickerPicker = ({ value, onChange, compact = false }) => {
  const [activeTab, setActiveTab] = useState("animals");
  const tab = TABS.find((t) => t.id === activeTab) || TABS[0];

  return (
    <div className="w-full" data-testid="sticker-picker">
      {/* Tab bar */}
      <div className="flex items-center gap-2 mb-3">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setActiveTab(t.id)}
            className={`px-3.5 py-1.5 rounded-full text-xs font-bold transition-all ${
              activeTab === t.id
                ? "bg-amber-400/20 border border-amber-400/50 text-amber-200"
                : "bg-white/[0.04] border border-white/10 text-white/50 hover:bg-white/[0.08]"
            }`}
            data-testid={`sticker-tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Sticker grid */}
      <div
        className={`grid gap-2 ${compact ? "grid-cols-5" : "grid-cols-5 sm:grid-cols-6 md:grid-cols-7"}`}
        data-testid={`sticker-grid-${activeTab}`}
      >
        {tab.stickers.map((s) => {
          const selected = value === s.id;
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => onChange?.(s.id)}
              title={s.label}
              aria-label={s.label}
              aria-pressed={selected}
              className={`aspect-square flex items-center justify-center rounded-xl border text-3xl sm:text-4xl transition-all ${
                selected
                  ? "border-amber-400 bg-amber-400/15 scale-105 shadow-lg shadow-amber-400/20"
                  : "border-white/10 bg-white/[0.04] hover:bg-white/[0.08] hover:border-white/20"
              }`}
              data-testid={`sticker-${s.id}`}
            >
              <span aria-hidden>{s.emoji}</span>
            </button>
          );
        })}
      </div>

      {value && (
        <p className="text-white/60 text-xs mt-3 text-center" data-testid="sticker-selected-label">
          Selected: <span className="text-amber-300 font-bold">{getStickerEmoji(value)}</span>
        </p>
      )}
    </div>
  );
};

export default StickerPicker;
